import type { ProductListingResolution } from "@adpilot/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { HTTPError } from "ky";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useIsVendorShop } from "@/hooks/use-shop-account-type";
import { resolveProductListings } from "@/lib/product-listing-api";
import { defaultStrategyName } from "@/lib/strategy-name";
import { type StrategySnapshot, strategySnapshotQuery } from "@/lib/strategy-snapshot";
import {
  buildProductStrategy,
  GENERIC_TACTIC_IDS,
  type Strategy,
} from "@/mocks/operating-strategy";
import { getProductGoalCaution, STRATEGY_PRODUCT } from "@/mocks/strategy-product";
import { useAuthStore } from "@/stores/auth";
import { useOperatingStrategyStore } from "@/stores/operating-strategy";

function listingOf(result: ProductListingResolution | undefined) {
  switch (result?.type) {
    case "resolved":
    case "inactive":
      return result.listing;
    case "ambiguous":
      // One ASIN can map to several SKUs; any of them carries the same title and image.
      return result.candidates[0] ?? null;
    default:
      return null;
  }
}

/**
 * Shared by the pre-report check and the product row, so the row reuses the
 * cached lookup. The catalog state tells a never-synced catalog apart from an
 * ASIN the shop does not own.
 */
export function shopProductQuery(shopId: string, asin: string) {
  return queryOptions({
    queryKey: ["product-listings", "resolve", shopId, asin],
    queryFn: async () => {
      const { results, catalog } = await resolveProductListings(shopId, [asin]);
      return { listing: listingOf(results[0]), catalog };
    },
    staleTime: 5 * 60_000,
  });
}

export const SALES_GOAL = "销售额最大化";
export const GOALS = [
  SALES_GOAL,
  "利润最大化",
  "排名最优化",
  "广告ROAS 最大化",
  "评论最优化",
] as const;
export const PRODUCT_STAGES = ["新品期", "成长期", "成熟期"] as const;
export const RANKS = ["1–10 名", "11–20 名", "21–50 名", "51–100 名", "100 名之后"] as const;
export const MODES = ["半自动", "全自动"] as const;

function parseAsin(value: string): string | null {
  const input = value.trim();
  if (/^[a-z0-9]{10}$/i.test(input)) return input.toUpperCase();
  try {
    const url = new URL(input);
    if (
      !["https:", "http:"].includes(url.protocol) ||
      !["amazon.com", "www.amazon.com"].includes(url.hostname)
    )
      return null;
    return (
      url.pathname.match(/\/(?:dp|gp\/product)\/([a-z0-9]{10})(?:\/|$)/i)?.[1]?.toUpperCase() ??
      null
    );
  } catch {
    return null;
  }
}
const productSchema = z.object({
  product: z
    .string()
    .trim()
    .min(1, "请输入商品链接或 ASIN。")
    .refine(
      (value) => !value || parseAsin(value) !== null,
      "请输入 10 位字母数字 ASIN，或有效的亚马逊商品详情链接。",
    ),
});
const settingsSchema = z
  .object({
    productStage: z
      .string()
      .refine((value) => PRODUCT_STAGES.some((stage) => stage === value), "请选择商品阶段。"),
    goal: z.string().refine((value) => GOALS.some((goal) => goal === value), "请选择业务目标。"),
    rank: z.string(),
    mode: z.string().min(1, "请选择执行模式。"),
  })
  .superRefine((value, ctx) => {
    if (value.goal === SALES_GOAL && !RANKS.some((rank) => rank === value.rank))
      ctx.addIssue({ code: "custom", path: ["rank"], message: "请选择目标小类排名。" });
  });
const nameSchema = z
  .string()
  .trim()
  .min(1, "请输入自定义策略名称。")
  .max(50, "自定义策略名称需为 1–50 字，首尾空格不计入。");

export type WizardStage = "research" | "settings" | "plan" | "complete";
export type FailureScenario =
  | "success"
  | "unauthorized"
  | "report-error"
  | "plan-error"
  | "create-error";

export function useStrategyWizard(tacticIds: string[], snapshot?: StrategySnapshot) {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  const shops = useAuthStore((s) => s.shops);
  const shopId = useAuthStore((s) => s.currentShopId);
  const vendor = useIsVendorShop(shopId);
  const productForm = useForm({
    resolver: zodResolver(productSchema),
    defaultValues: { product: "" },
  });
  const settingsForm = useForm({
    resolver: zodResolver(settingsSchema),
    defaultValues: { productStage: "", goal: "", rank: "", mode: "" },
  });
  const [stage, setStage] = useState<WizardStage>("research");
  const [asin, setAsin] = useState("");
  const [reportReady, setReportReady] = useState(false);
  const [busy, setBusy] = useState<"check" | "report" | "plan" | "create" | null>(null);
  const [error, setError] = useState("");
  // Keep failure states reproducible without adding controls to the product UI.
  const [scenario, setScenario] = useState<FailureScenario>(() => {
    if (!import.meta.env.DEV) return "success";
    const value = new URLSearchParams(window.location.search).get("strategyScenario");
    return value === "unauthorized" ||
      value === "report-error" ||
      value === "plan-error" ||
      value === "create-error"
      ? value
      : "success";
  });
  const [unauthorized, setUnauthorized] = useState(false);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [planSignature, setPlanSignature] = useState<string | null>(null);
  const [created, setCreated] = useState<Strategy | null>(null);
  const generation = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const pending = useRef(false);
  const creationId = useRef(crypto.randomUUID());
  const settings = settingsForm.watch();
  const signature = JSON.stringify(settings);
  const validPlan = planSignature === signature;
  const dirty =
    stage !== "complete" && Boolean(productForm.formState.isDirty || asin || stage !== "research");

  const cancelPending = useCallback(() => {
    generation.current += 1;
    clearTimeout(timer.current);
    pending.current = false;
    setBusy(null);
  }, []);
  const interruptPending = useCallback(() => {
    if (!pending.current) return;
    cancelPending();
    setError("操作已取消，当前输入已保留，可重试。");
  }, [cancelPending]);
  useEffect(
    () => () => {
      generation.current += 1;
      clearTimeout(timer.current);
    },
    [],
  );
  function simulate(kind: "report" | "plan" | "create", success: () => void) {
    if (pending.current) return;
    pending.current = true;
    setBusy(kind);
    setError("");
    const token = ++generation.current;
    const fails = scenario === `${kind}-error`;
    timer.current = setTimeout(
      () => {
        if (token !== generation.current) return;
        pending.current = false;
        setBusy(null);
        if (fails) {
          setScenario("success");
          setError(
            kind === "report"
              ? "商品分析资料加载失败，请重试，已选商品会保留。"
              : kind === "plan"
                ? "策略决策生成失败，请重试，当前设定已保留。"
                : "保存失败，当前方案已保留。",
          );
        } else success();
      },
      kind === "report" ? 30_000 : 1200,
    );
  }
  /**
   * Runs the pre-report lookup under the shared busy/cancel bookkeeping.
   * Resolves to null when the lookup was cancelled meanwhile.
   */
  async function runCheck<T>(
    lookup: () => Promise<T>,
  ): Promise<{ ok: true; value: T } | { ok: false; error: unknown } | null> {
    pending.current = true;
    setBusy("check");
    setError("");
    const token = ++generation.current;
    try {
      const value = await lookup();
      if (token !== generation.current) return null;
      pending.current = false;
      setBusy(null);
      return { ok: true, value };
    } catch (error) {
      if (token !== generation.current) return null;
      cancelPending();
      return { ok: false, error };
    }
  }
  function startReport(nextAsin: string) {
    setAsin(nextAsin);
    setName(defaultStrategyName(nextAsin));
    simulate("report", () => setReportReady(true));
  }
  const generateReport = productForm.handleSubmit(async ({ product }) => {
    if (pending.current) return;
    const nextAsin = parseAsin(product);
    if (!nextAsin) return;
    if (scenario === "unauthorized") {
      setUnauthorized(true);
      return;
    }
    const demo = nextAsin === STRATEGY_PRODUCT.asin;
    // A seller stays gated by the fixed demo product (#551). A vendor's catalog
    // is keyed by ASIN (#673), so its own ASINs resolve there and any other
    // ASIN asks for authorization (#677).
    const catalogShopId = demo || !vendor ? null : shopId;
    if (!demo && !catalogShopId) {
      setError("");
      setUnauthorized(true);
      return;
    }
    const check = await runCheck(async () => {
      if (!catalogShopId) {
        await queryClient.fetchQuery(
          strategySnapshotQuery(
            userId ?? "",
            shops.map((shop) => shop.shopId),
          ),
        );
        return "owned" as const;
      }
      // Always ask the API here so a retry sees a catalog that synced meanwhile;
      // the product row then reads this fresh result from the cache.
      const { listing, catalog } = await queryClient.fetchQuery({
        ...shopProductQuery(catalogShopId, nextAsin),
        staleTime: 0,
      });
      if (listing) return "owned" as const;
      // A catalog that was never fetched cannot say whose ASIN this is. A partial
      // vendor catalog never records a complete sync, so only attempts count.
      const unsynced = catalog.lastAttemptAt === null || catalog.completeness === "unavailable";
      return unsynced ? ("unsynced" as const) : ("foreign" as const);
    });
    if (!check) return;
    if (!check.ok) {
      if (check.error instanceof HTTPError && check.error.response.status === 403) {
        setUnauthorized(true);
        return;
      }
      setError("商品分析资料加载失败，请重试，已填写的商品会保留。");
      return;
    }
    if (check.value === "foreign") {
      setUnauthorized(true);
      return;
    }
    if (check.value === "unsynced") {
      setError("当前店铺的商品目录尚未同步完成，请稍后重试，已填写的商品会保留。");
      return;
    }
    // The demo ASIN may belong to another authorized shop than the currently selected one.
    startReport(nextAsin);
  });
  const generatePlan = settingsForm.handleSubmit((values) => {
    if (pending.current) return;
    setStage("plan");
    setPlanSignature(null);
    simulate("plan", () => {
      setPlanSignature(JSON.stringify(values));
    });
  });
  function changeSetting(field: "productStage" | "goal" | "rank" | "mode", value: string) {
    if (settingsForm.getValues(field) !== value) setPlanSignature(null);
    settingsForm.setValue(field, value, { shouldValidate: true });
    if (field === "goal" && value !== SALES_GOAL) settingsForm.setValue("rank", "");
    setError("");
  }
  function createStrategy() {
    if (!validPlan || pending.current || created) return;
    const parsed = nameSchema.safeParse(name);
    if (!parsed.success) {
      setNameError(parsed.error.issues[0]?.message ?? "请输入自定义策略名称。");
      return;
    }
    setNameError("");
    simulate("create", () => {
      const store = useOperatingStrategyStore.getState();
      const names = new Set(store.strategies.map((strategy) => strategy.name));
      let uniqueName = parsed.data;
      for (let suffix = 2; names.has(uniqueName); suffix++) {
        const tail = `-${String(suffix).padStart(2, "0")}`;
        uniqueName = `${parsed.data.slice(0, 50 - tail.length)}${tail}`;
      }
      const productStrategy =
        snapshot && asin === STRATEGY_PRODUCT.asin
          ? buildProductStrategy(snapshot, creationId.current, uniqueName, settings.goal)
          : {
              id: creationId.current,
              name: uniqueName,
              source: "ai" as const,
              updatedAt: "",
              tacticIds,
              campaigns: ["长尾精准", "自动探索", "转化词承接"].map(
                (label) => `${uniqueName}-SP-${label}`,
              ),
            };
      const strategy: Strategy = {
        ...productStrategy,
        asin,
        productStage: settings.productStage,
        goal: settings.goal === SALES_GOAL ? `${settings.goal} · ${settings.rank}` : settings.goal,
        mode: settings.mode,
        tacticIds: asin === STRATEGY_PRODUCT.asin ? tacticIds : [...GENERIC_TACTIC_IDS],
      };
      if (snapshot && asin === STRATEGY_PRODUCT.asin) {
        strategy.sections = [
          ...(strategy.sections ?? []),
          {
            title: "目标核对",
            body: `已保留“${strategy.goal}”目标。${getProductGoalCaution(settings.goal)}`,
          },
        ];
      }
      store.addStrategy(strategy);
      setName(uniqueName);
      setCreated(strategy);
      setStage("complete");
    });
  }
  function reset() {
    cancelPending();
    productForm.reset();
    settingsForm.reset();
    setStage("research");
    setAsin("");
    setReportReady(false);
    setPlanSignature(null);
    setCreated(null);
    setName("");
    setNameError("");
    setError("");
    setUnauthorized(false);
    creationId.current = crypto.randomUUID();
  }
  function changeProduct() {
    cancelPending();
    setName("");
    setNameError("");
    setStage("research");
    setReportReady(false);
    setAsin("");
    settingsForm.reset();
    setPlanSignature(null);
    setError("");
    setCreated(null);
    creationId.current = crypto.randomUUID();
  }
  return {
    interruptPending,
    productForm,
    settingsForm,
    settings,
    stage,
    setStage,
    asin,
    reportReady,
    busy,
    error,
    setError,
    scenario,
    setScenario,
    unauthorized,
    setUnauthorized,
    name,
    setName,
    nameError,
    setNameError,
    planSignature,
    validPlan,
    created,
    dirty,
    generateReport,
    generatePlan,
    changeSetting,
    createStrategy,
    reset,
    changeProduct,
  };
}
